import React, { Component } from 'react';
import {
    Panel,
    PanelType,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Separator
} from '@fluentui/react';
import { DestinationType, IFileProvisionConfig, getDestinationTypeColor } from './types';

interface IProps {
    isOpen: boolean;
    config: IFileProvisionConfig | null;
    onDismiss: () => void;
    onEdit: (config: IFileProvisionConfig) => void;
}

class ProvisionConfigDetailsPanel extends Component<IProps> {
    private _convertUtcToLocalTime = (utcTimeString: string | null | undefined): string | null => {
        if (!utcTimeString) return null;
        
        // Assume the time from server is UTC, create a proper UTC date
        const utcDate = new Date(utcTimeString + 'Z');
        
        // Convert to local time and format
        return utcDate.toLocaleString();
    };

    private _parseCronExpression = (cron: string): string => {
        if (!cron) return '';
        
        const parts = cron.trim().split(/\s+/);
        if (parts.length < 5) return 'Invalid cron format';
        
        // Cron format: second minute hour day month dayOfWeek
        // or: minute hour day month dayOfWeek (5 parts)
        const hasSeconds = parts.length === 6;
        const offset = hasSeconds ? 1 : 0;
        
        const minute = parts[offset];
        const hour = parts[offset + 1];
        const day = parts[offset + 2];
        const month = parts[offset + 3];
        const dayOfWeek = parts[offset + 4];
        
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        
        let description = 'Runs ';
        
        // Day of week specific
        if (dayOfWeek !== '*' && dayOfWeek !== '?') {
            const dayNum = parseInt(dayOfWeek);
            if (!isNaN(dayNum) && dayNum >= 0 && dayNum <= 6) {
                description += `every ${dayNames[dayNum]} `;
            }
        }
        // Day of month specific
        else if (day !== '*' && day !== '?') {
            description += `on day ${day} of the month `;
        }
        // Daily
        else {
            description += 'daily ';
        }
        
        // Time
        if (hour !== '*' && minute !== '*') {
            const h = parseInt(hour);
            const m = parseInt(minute);
            if (!isNaN(h) && !isNaN(m)) {
                description += `at ${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            }
        } else if (hour !== '*') {
            description += `every hour at minute ${minute}`;
        } else if (minute !== '*') {
            description += `every minute ${minute} of each hour`;
        }
        
        // Month specific
        if (month !== '*') {
            const monthNum = parseInt(month);
            if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
                description += ` in ${monthNames[monthNum]}`;
            }
        }
        
        return description;
    };

    private _renderField = (label: string, value: string | undefined | null, monospace: boolean = false): JSX.Element => {
        return (
            <div style={{ marginBottom: 15 }}>
                <div style={{ 
                    fontSize: '12px', 
                    fontWeight: 600, 
                    color: '#605e5c', 
                    marginBottom: 4,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {label}
                </div>
                <div style={{
                    fontSize: '14px',
                    color: '#323130',
                    fontFamily: monospace ? 'Consolas, monospace' : 'inherit',
                    wordBreak: 'break-word'
                }}>
                    {value || <span style={{ color: '#a19f9d', fontStyle: 'italic' }}>Not set</span>}
                </div>
            </div>
        );
    };

    private _renderBadge = (text: string, color: string): JSX.Element => {
        return (
            <div style={{
                display: 'inline-block',
                padding: '6px 14px',
                borderRadius: '12px',
                backgroundColor: `${color}20`,
                color: color,
                fontWeight: 600,
                fontSize: '13px',
                border: `1px solid ${color}40`
            }}>
                {text}
            </div>
        );
    };

    render() {
        const { isOpen, onDismiss, config, onEdit } = this.props;

        if (!config) return null;

        const destinationColor = getDestinationTypeColor(config.destinationType);

        return (
            <Panel
                isOpen={isOpen}
                onDismiss={onDismiss}
                headerText="Configuration Details"
                closeButtonAriaLabel="Close"
                onRenderFooterContent={() => (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <DefaultButton
                            onClick={() => onEdit(config)}
                            iconProps={{ iconName: 'Edit' }}
                        >
                            Edit Configuration
                        </DefaultButton>
                        <DefaultButton onClick={onDismiss}>
                            Close
                        </DefaultButton>
                    </div>
                )}
                isFooterAtBottom={true}
                customWidth='550px'
                type={PanelType.medium}
                isLightDismiss
            >
                {/* Status Banner */}
                <MessageBar
                    messageBarType={config.isActive ? MessageBarType.success : MessageBarType.warning}
                    isMultiline={false}
                    styles={{ root: { marginBottom: 20 } }}
                >
                    {config.isActive ? 
                        'This configuration is active and will run on schedule' : 
                        'This configuration is currently inactive'}
                </MessageBar>

                {/* Basic Info Section */}
                <div style={{ marginBottom: 25 }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: 600 }}>
                        Basic Information
                    </h3>
                    
                    <div style={{ display: 'flex', gap: '10px', marginBottom: 15 }}>
                        {this._renderBadge(config.destinationType, destinationColor)}
                        {this._renderBadge(config.isActive ? 'ACTIVE' : 'INACTIVE', config.isActive ? '#107c10' : '#a19f9d')}
                    </div>

                    {this._renderField('Configuration ID', `#${config.id}`)}
                </div>

                <Separator />

                {/* Destination Details Section */}
                <div style={{ marginBottom: 25, marginTop: 20 }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: 600 }}>
                        Destination Details
                    </h3>
                    
                    {config.destinationType === DestinationType.sharepoint && (
                        <>
                            {this._renderField('Site URL', config.siteUrl)}
                            {this._renderField('Drive ID', config.driveId, true)}
                            {this._renderField('Folder Path', config.folderPath)}
                        </>
                    )}

                    {config.destinationType === DestinationType.blobContainer && (
                        <>
                            {this._renderField('Container Name', config.containerName)}
                            {this._renderField('Target URI', config.targetUri)}
                        </>
                    )}

                    {(config.destinationType === 'FileShare' || config.destinationType === 'FTP') && (
                        this._renderField('Target URI', config.targetUri)
                    )}
                </div>

                <Separator />

                {/* Schedule Section */}
                <div style={{ marginBottom: 25, marginTop: 20 }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: 600 }}>
                        Schedule & Execution
                    </h3>
                    {config.frequencyCron && (
                        <div style={{ marginBottom: 15 }}>
                            <div style={{ 
                                fontSize: '12px', 
                                fontWeight: 600, 
                                color: '#605e5c', 
                                marginBottom: 4,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Frequency (Cron Expression)
                            </div>
                            <div style={{
                                fontSize: '14px',
                                color: '#323130',
                                fontFamily: 'Consolas, monospace',
                                marginBottom: 8
                            }}>
                                {config.frequencyCron}
                            </div>
                            <div style={{
                                padding: '10px 12px',
                                backgroundColor: '#e6f4ff',
                                borderRadius: '4px',
                                border: '1px solid #91caff',
                                fontSize: '13px',
                                color: '#0050b3',
                                fontWeight: 500
                            }}>
                                {this._parseCronExpression(config.frequencyCron)}
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: '#8c8c8c',
                                marginTop: 6,
                                fontStyle: 'italic'
                            }}>
                                Format: {config.frequencyCron.split(/\s+/).length === 6 ? 'second minute hour day month dayOfWeek' : 'minute hour day month dayOfWeek'}
                            </div>
                        </div>
                    )}
                    {this._renderField('Last Run', config.lastRunAt ? new Date(config.lastRunAt + 'Z').toLocaleString() : null)}
                </div>

                <Separator />

                {/* Notes Section */}
                {config.notes && (
                    <div style={{ marginTop: 20 }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: 600 }}>
                            Notes
                        </h3>
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#fffef5',
                            borderRadius: '4px',
                            border: '1px solid #fff4ce',
                            fontSize: '14px',
                            color: '#323130',
                            lineHeight: '1.5'
                        }}>
                            {config.notes}
                        </div>
                    </div>
                )}
            </Panel>
        );
    }
}

export default ProvisionConfigDetailsPanel;
